import { useCallback, useEffect, useRef, useState } from 'react';

export type LiveState = 'live' | 'refreshing' | 'paused' | 'stale';
export type RefreshMode = 'Auto' | '15s' | '30s' | '1m' | '5m' | 'Paused';

const fixedIntervals: Record<Exclude<RefreshMode, 'Auto' | 'Paused'>, number> = {
  '15s': 15_000,
  '30s': 30_000,
  '1m': 60_000,
  '5m': 300_000,
};

export function refreshInterval(
  view: string,
  hidden: boolean,
  range = '24h',
  mode: RefreshMode = 'Auto',
) {
  if (mode === 'Paused') return Number.POSITIVE_INFINITY;
  if (hidden) return 180_000;
  if (mode !== 'Auto') return fixedIntervals[mode];
  if (view === 'NOC Wallboard' || ['Logs', 'Incidents'].includes(view)) return 15_000;
  if (view === 'Overview' || (view === 'Assets' && ['1h', '6h'].includes(range))) return 30_000;
  if (range === '30d') return 300_000;
  if (range === '7d') return 120_000;
  if (view === 'Reports') return Number.POSITIVE_INFINITY;
  return 60_000;
}

export function liveState(
  lastUpdated: string,
  hidden: boolean,
  now = Date.now(),
  refreshing = false,
  failed = false,
): LiveState {
  if (hidden) return 'paused';
  if (refreshing) return 'refreshing';
  if (failed || now - new Date(lastUpdated).getTime() > 120_000) return 'stale';
  return 'live';
}

export function staleWhileRevalidateValue<T>(
  lastGood: T | null,
  incoming: T | null,
  failed = false,
) {
  return failed || incoming == null ? lastGood : incoming;
}

type Entry = {
  id: number;
  view: string;
  range: string;
  mode: RefreshMode;
  callback: () => void | Promise<void>;
  nextAt: number;
};
class RefreshScheduler {
  private entries = new Map<number, Entry>();
  private timer: number | null = null;
  private sequence = 0;
  private running = false;
  constructor() {
    if (typeof document !== 'undefined')
      document.addEventListener('visibilitychange', this.visibility);
  }
  subscribe(view: string, range: string, mode: RefreshMode, callback: Entry['callback']) {
    const id = ++this.sequence;
    this.entries.set(id, {
      id,
      view,
      range,
      mode,
      callback,
      nextAt: Date.now() + refreshInterval(view, document.hidden, range, mode),
    });
    this.schedule();
    return () => {
      this.entries.delete(id);
      this.schedule();
    };
  }
  private visibility = () => {
    const now = Date.now();
    for (const entry of this.entries.values()) entry.nextAt = document.hidden ? now + 180_000 : now;
    this.schedule();
  };
  private schedule = () => {
    if (this.timer != null) window.clearTimeout(this.timer);
    const nextAt = Math.min(...[...this.entries.values()].map((entry) => entry.nextAt));
    if (!Number.isFinite(nextAt)) {
      this.timer = null;
      return;
    }
    this.timer = window.setTimeout(this.tick, Math.max(0, nextAt - Date.now()));
  };
  private tick = async () => {
    if (this.running) return this.schedule();
    this.running = true;
    const now = Date.now();
    const due = [...this.entries.values()].filter((entry) => entry.nextAt <= now);
    await Promise.allSettled(due.map((entry) => entry.callback()));
    for (const entry of due)
      entry.nextAt =
        Date.now() + refreshInterval(entry.view, document.hidden, entry.range, entry.mode);
    this.running = false;
    this.schedule();
  };
}
let scheduler: RefreshScheduler | null = null;
function sharedScheduler() {
  return (scheduler ??= new RefreshScheduler());
}

export function useLiveRefresh(
  view: string,
  refresh: () => void | Promise<void>,
  options: { range?: string; mode?: RefreshMode } = {},
) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const [lastUpdated, setLastUpdated] = useState(() => new Date().toISOString());
  const [hidden, setHidden] = useState(() => document.hidden);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const runningRef = useRef(false);
  const run = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRefreshing(true);
    try {
      await refreshRef.current();
      setLastUpdated(new Date().toISOString());
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      runningRef.current = false;
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    const visibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', visibility);
    const unsubscribe = sharedScheduler().subscribe(
      view,
      options.range ?? '24h',
      options.mode ?? 'Auto',
      run,
    );
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [view, options.range, options.mode, run]);
  return {
    lastUpdated,
    state: liveState(lastUpdated, hidden, Date.now(), refreshing, failed),
    refresh: run,
    refreshing,
    delayed: failed,
  };
}
